import { Component, TemplateRef } from '@angular/core';
import { HttpService } from 'src/app/http.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-agent-report',
  templateUrl: './agent-report.component.html',
  styleUrls: ['./agent-report.component.scss']
})
export class AgentReportComponent {

  public data: any = { type: 'last7' };
  public logs: any[] = [];
  public filtered_logs: any[] = [];
  public dates: any[] = [];
  public is_loading_logs: boolean = false;
  public is_loading_detail: boolean = false;
  public today: string = new Date().toISOString().split('T')[0];
  public search: string = '';
  public sort_key: string = 'commission';
  public sort_dir: 'asc' | 'desc' = 'desc';
  public commissionDetail: any = null;
  public selected_log: any = null;
  public configuration: any = {};
  // detail modal: 'simple' = plain-language receipt, 'advanced' = raw number table
  public view_mode: 'simple' | 'advanced' = 'simple';

  // custom range
  public custom_from: string = '';
  public custom_to: string = '';

  constructor(private http: HttpService, private ngbModal: NgbModal) {
    this.generateDates();
    this.getConfiguration();
    // default custom range: this month
    const now = new Date();
    this.custom_from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    this.custom_to = this.today;
  }

  async getConfiguration() {
    let rtn: any = await this.http.post('/admin/company/setting');
    this.configuration = rtn.setting || {};
  }

  getDateRange(): { from: string, to: string } {
    const now = new Date();
    const fmt = (d: Date) => d.toISOString().split('T')[0];

    switch (this.data.type) {
      case 'today':
        return { from: this.today, to: this.today };
      case 'yesterday': {
        const yd = new Date(now);
        yd.setDate(yd.getDate() - 1);
        return { from: fmt(yd), to: fmt(yd) };
      }
      case 'last7': {
        const d = new Date(now);
        d.setDate(d.getDate() - 6);
        return { from: fmt(d), to: this.today };
      }
      case 'last30': {
        const d = new Date(now);
        d.setDate(d.getDate() - 29);
        return { from: fmt(d), to: this.today };
      }
      case 'this_month': {
        const d = new Date(now.getFullYear(), now.getMonth(), 1);
        return { from: fmt(d), to: this.today };
      }
      case 'last_month': {
        const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const last = new Date(now.getFullYear(), now.getMonth(), 0);
        return { from: fmt(first), to: fmt(last) };
      }
      default:
        return { from: '', to: '' };
    }
  }

  async filter() {
    this.is_loading_logs = true;
    this.commissionDetail = null;

    const payload: any = { type: this.data.type, search: this.search };

    if (this.data.type === 'week') {
      payload.date = this.dates.find(d => d.value === this.data.week_number);
    } else if (this.data.type === 'day') {
      payload.date = this.data.date;
      payload.type = 'day';
    } else if (this.data.type === 'custom') {
      if (!this.custom_from || !this.custom_to) {
        this.is_loading_logs = false;
        return;
      }
      payload.from = this.custom_from;
      payload.to = this.custom_to;
    } else {
      // Quick presets: today, yesterday, last7, last30, this_month, last_month
      const range = this.getDateRange();
      payload.type = 'custom';
      payload.from = range.from;
      payload.to = range.to;
    }

    const rtn: any = await this.http.post('/admin/agent-log', payload);
    if (rtn.status) {
      this.logs = rtn.agents || [];
    } else {
      this.logs = [];
    }
    this.applySort();
    this.is_loading_logs = false;
  }

  applyLocalSearch() {
    const q = this.search.trim().toLowerCase();
    if (!q) {
      this.filtered_logs = [...this.logs];
    } else {
      this.filtered_logs = this.logs.filter(l =>
        (l.name && l.name.toLowerCase().includes(q)) ||
        (l.mobile && l.mobile.toLowerCase().includes(q)) ||
        (l.code && l.code.toLowerCase().includes(q))
      );
    }
    this.sortFilteredLogs();
  }

  sortBy(key: string) {
    if (this.sort_key === key) {
      this.sort_dir = this.sort_dir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sort_key = key;
      this.sort_dir = ['commission', 'total_in', 'total_out', 'total_profit', 'lifetime_profit', 'total_wallet_withdraw', 'wallet_balance', 'withdraw_count'].includes(key) ? 'desc' : 'asc';
    }
    this.sortFilteredLogs();
  }

  applySort() {
    this.filtered_logs = [...this.logs];
    this.sortFilteredLogs();
  }

  sortFilteredLogs() {
    const key = this.sort_key;
    const dir = this.sort_dir === 'asc' ? 1 : -1;
    this.filtered_logs = [...this.filtered_logs].sort((a, b) => {
      const av = isNaN(a[key]) ? (a[key] || '').toString().toLowerCase() : parseFloat(a[key] || 0);
      const bv = isNaN(b[key]) ? (b[key] || '').toString().toLowerCase() : parseFloat(b[key] || 0);
      return av > bv ? dir : av < bv ? -dir : 0;
    });
  }

  sortIcon(key: string): string {
    if (this.sort_key !== key) return '⇅';
    return this.sort_dir === 'asc' ? '↑' : '↓';
  }

  sum(key: string): number {
    return this.filtered_logs.reduce((acc, log) => acc + parseFloat(log[key] ?? 0), 0);
  }

  switchType() {
    if (this.data.type === 'day') {
      this.data.date = this.today;
    }
    this.filter();
  }

  generateDates() {
    this.dates = [];
    const today = new Date();
    const day = today.getDay();
    const lastSunday = new Date(today);
    lastSunday.setDate(today.getDate() - day - 7);

    for (let i = 0; i < 13; i++) {
      const weekStart = new Date(lastSunday);
      weekStart.setDate(lastSunday.getDate() - i * 7);

      const firstJan = new Date(weekStart.getFullYear(), 0, 1);
      const days = Math.floor((weekStart.getTime() - firstJan.getTime()) / 86400000);
      const weekNumber = Math.floor((days + firstJan.getDay()) / 7) + 1;

      const monday = new Date(weekStart);
      monday.setDate(weekStart.getDate() + 1);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);

      const fmt = (d: Date) =>
        ('0' + d.getDate()).slice(-2) + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + d.getFullYear();

      this.dates.push({
        year: weekStart.getFullYear(),
        week_number: weekNumber,
        value: weekNumber + '-' + weekStart.getFullYear(),
        label: 'Week ' + weekNumber + ': ' + fmt(monday) + ' to ' + fmt(sunday),
        from: fmt(monday),
        to: fmt(sunday)
      });
    }

    this.data.week_number = this.dates[0].value;
    this.data.date = this.today;
    this.filter();
  }

  findWeekLabel(): string {
    const match = this.dates.find(d => d.value === this.data.week_number);
    return match ? match.label : '';
  }

  async openDetail(content: TemplateRef<any>, log: any) {
    this.selected_log = log;
    this.commissionDetail = null;
    this.is_loading_detail = true;
    this.ngbModal.open(content, { size: 'xl' });

    const rtn: any = await this.http.post('/admin/commission-detail', {
      from: log.from, to: log.to, player_id: log.id
    });
    if (rtn.status) {
      this.commissionDetail = rtn;
      if (rtn.report?.detail?.length > 0 && rtn.report.detail[0].breakdown) {
        try {
          const stored = typeof rtn.report.detail[0].breakdown === 'string'
            ? JSON.parse(rtn.report.detail[0].breakdown)
            : rtn.report.detail[0].breakdown;
          if (stored.report) {
            this.commissionDetail.breakdown = stored.report;
          }
        } catch (e) { }
      }
    }
    this.is_loading_detail = false;
  }

  periodLabel(): string {
    if (this.data.type === 'week') return this.findWeekLabel();
    if (this.data.type === 'day') return this.data.date || '';
    if (this.data.type === 'custom') return (this.custom_from || '') + ' to ' + (this.custom_to || '');
    // Quick presets
    const range = this.getDateRange();
    return range.from + ' to ' + range.to;
  }

  getNextBF(day: any): number {
    const b = day.breakdown || {};
    const todayProfit = b.today_profit ?? null;
    if (todayProfit !== null) {
      return todayProfit >= 0 ? 0 : todayProfit;
    }
    // fallback: compute from available fields
    const deposit = parseFloat(b.report?.total_deposit || b.total_deposit || 0);
    const withdraw = parseFloat(b.report?.total_withdraw || b.total_withdraw || 0);
    const agentBalance = parseFloat(b.report?.agent_balance || b.agent_balance || 0);
    const rollingDeduction = parseFloat(b.rolling_deduction || 0);
    const bonusDeduction = parseFloat(b.bonus_deduction || 0);
    const profit = deposit - withdraw + agentBalance - rollingDeduction - bonusDeduction;
    return profit >= 0 ? 0 : profit;
  }

  /**
   * Turns the stored commission breakdown for one day into a top-to-bottom,
   * plain-language "receipt" anyone can read — instead of a dense number row.
   * Returns { mode, rows[], final, finalNegative, note }.
   * Each row: { op, label, cn, value, neg?, strong?, final?, isRate? }
   *   op    = the leading symbol shown ('' | '−' | '+' | '×' | '=')
   *   neg   = render the amount in red / parentheses (a subtraction)
   *   strong= subtotal line (bold)
   *   final = the final commission line
   *   isRate= value is a percentage, not a currency amount
   */
  calcReceipt(day: any): any {
    const b = day?.breakdown || {};
    const net = Number(day?.commission || 0);
    const deductions = Array.isArray(b.deductions) ? b.deductions : [];
    const childTotal = deductions.reduce((s: number, d: any) => s + Number(d.amount || 0), 0);
    const rows: any[] = [];

    // ----- RED-SHARE (wallet_balance) mode -----
    if (b.mode === 'wallet_balance') {
      const rawProfit = Number(b.raw_profit || 0);
      const rolling = Number(b.rolling_deduction ?? b.total_rolling_paid ?? 0);
      const bucket = Number(b.bucket_deduction || 0);
      const rate = Number(b.rate || 0);
      const basis = rawProfit - rolling - bucket;
      const gross = (b.gross != null) ? Number(b.gross) : (basis * rate / 100);

      rows.push({ op: '', label: 'Downline lost this much (the basis)', cn: '下线输了这么多（计算基数）', value: rawProfit });
      if (Math.abs(rolling) >= 0.0005)
        rows.push({ op: '−', label: 'Water (rolling) already paid to players', cn: '减：已派给玩家的返水', value: rolling, neg: true });
      if (Math.abs(bucket) >= 0.0005)
        rows.push({ op: '−', label: 'Company bucket share', cn: '减：公司抽成', value: bucket, neg: true });
      rows.push({ op: '=', label: 'Your basis after deductions', cn: '扣除后的计算基数', value: basis, strong: true });
      rows.push({ op: '×', label: 'Your commission rate', cn: '你的佣金比率', value: rate, isRate: true });
      rows.push({ op: '=', label: 'Your gross commission', cn: '你的毛佣金', value: gross, strong: true });
      for (const d of deductions)
        rows.push({ op: '−', label: 'Downline agent ' + (d.code || ('#' + d.agent_id)) + ' already earned', cn: '减：下线代理 ' + (d.code || ('#' + d.agent_id)) + ' 已赚取的部分', value: Number(d.amount || 0), neg: true });
      rows.push({ op: '=', label: 'Your final commission', cn: '你的最终佣金', value: net, final: true });

      let note = '';
      if (net < 0) {
        note = 'This day is negative because your downline agents earned ' + childTotal.toFixed(2)
          + ' in total, but your tree was only allowed to pay out ' + gross.toFixed(2)
          + ' (your rate × the family\'s net loss). As the top agent you absorb the difference of '
          + net.toFixed(2) + '. It is NOT money lost at the table — it keeps the company at its agreed split. '
          + 'On days your own players lose big, this turns positive again.';
      }
      return { mode: 'red-share', rows, final: net, finalNegative: net < 0, note };
    }

    // ----- FORMULA / cash-flow mode -----
    const rep = b.report || b;
    const deposit = Number(rep.total_deposit || 0);
    const withdraw = Number(rep.total_withdraw || 0);
    const lose = Number(rep.total_lose || 0);
    const win = Number(rep.total_win || 0);
    const prevBF = Number(rep.agent_balance || 0);
    const rolling = Number(b.rolling_deduction || 0);
    const bonus = Number(b.bonus_deduction || 0);
    const basis = (b.today_profit != null) ? Number(b.today_profit) : (deposit - withdraw + prevBF - rolling - bonus);
    const gross = Number(b.formula_result ?? day?.commission ?? 0);
    const usesWinloss = (lose || win) && !(deposit || withdraw);

    if (usesWinloss) {
      rows.push({ op: '', label: 'Downline total losses', cn: '下线总输', value: lose });
      if (Math.abs(win) >= 0.0005)
        rows.push({ op: '−', label: 'Downline total wins', cn: '减：下线总赢', value: win, neg: true });
    } else {
      rows.push({ op: '', label: 'Downline deposits', cn: '下线存款', value: deposit });
      if (Math.abs(withdraw) >= 0.0005)
        rows.push({ op: '−', label: 'Downline withdrawals', cn: '减：下线提款', value: withdraw, neg: true });
    }
    if (Math.abs(prevBF) >= 0.0005)
      rows.push({ op: prevBF < 0 ? '−' : '+', label: 'Previous balance brought forward', cn: prevBF < 0 ? '减：上期亏损结转' : '加：上期结转', value: Math.abs(prevBF), neg: prevBF < 0 });
    if (Math.abs(rolling) >= 0.0005)
      rows.push({ op: '−', label: 'Water (rolling) deduction', cn: '减：返水', value: rolling, neg: true });
    if (Math.abs(bonus) >= 0.0005)
      rows.push({ op: '−', label: 'Bonus deduction', cn: '减：红利', value: bonus, neg: true });
    rows.push({ op: '=', label: 'Basis for the day', cn: '当日计算基数', value: basis, strong: true });
    if (Math.abs(Number(rep.agent_rate || 0)) >= 0.0005)
      rows.push({ op: '×', label: 'Your commission rate', cn: '你的佣金比率', value: Number(rep.agent_rate || 0), isRate: true });
    rows.push({ op: '=', label: 'Gross commission', cn: '毛佣金', value: gross, strong: true });
    for (const d of deductions)
      rows.push({ op: '−', label: 'Downline agent ' + (d.code || ('#' + d.agent_id)) + ' already earned', cn: '减：下线代理 ' + (d.code || ('#' + d.agent_id)) + ' 已赚取的部分', value: Number(d.amount || 0), neg: true });
    rows.push({ op: '=', label: 'Final commission', cn: '最终佣金', value: net, final: true });

    let note = '';
    if (basis < 0) {
      note = 'The basis was negative this day (' + basis.toFixed(2) + '), so no commission is paid and the '
        + 'shortfall is carried forward to the next day (shown as Next B/F). Commission resumes once the agent '
        + 'recovers back to positive.';
    } else if (net < 0) {
      note = 'Negative because downline agents earned more than this agent\'s gross. The top agent absorbs the '
        + 'difference to keep the company at its agreed split.';
    }
    return { mode: 'formula', rows, final: net, finalNegative: net < 0, note };
  }

  agentFormulaText(day: any): string {
    const b = day?.breakdown || {};
    if (b.mode !== 'wallet_balance') return day?.formula_param || '-';
    const rawProfit = Number(b.raw_profit || 0);
    const rolling = Number(b.rolling_deduction ?? b.total_rolling_paid ?? 0);
    const bucket = Number(b.bucket_deduction || 0);
    const rate = Number(b.rate || 0);
    const gross = Number(b.gross || 0);
    const childGross = Array.isArray(b.deductions)
      ? b.deductions.reduce((sum: number, d: any) => sum + Number(d.amount || 0), 0)
      : 0;
    const net = Number(day?.commission || 0);
    const base = `(${rawProfit.toFixed(3)} - ${rolling.toFixed(3)} - ${bucket.toFixed(3)}) × ${rate}%`;
    if (Math.abs(childGross) >= 0.001) {
      return `${base} - child agents ${childGross.toFixed(3)} = ${net.toFixed(3)}`;
    }
    return `${base} = ${gross.toFixed(3)}`;
  }
}
